import axios from 'axios';
import ConvertApi from 'convertapi-js';
import loadImage from 'image-promise';
// @ts-ignore
import psdjs from 'psd.js';
import { base64ToBlob } from './base64';
import { blobToFile } from './file';
import { getOssSize, uploadFile } from './oss';
import { svg2png } from './svg';

export const convertAPI = 'https://api.shareuc.com/resize';

const basicTypes = ['jpeg', 'jpg', 'png'];
const ossTypes = [
  'jpeg',
  'jpg',
  'png',
  'bmp',
  'gif',
  'webp',
  'tif',
  'tiff',
  'heic',
  'avif',
];
const apiTypes = ['jpeg', 'jpg', 'png', 'pdf', 'ai', 'heic'];
const localTypes = ['eps', 'psd', 'svg'];

const checkType = (file: File, suffix: string) => {
  let isValid = true;
  if (ossTypes.includes(suffix) && file.size < 20 * 1024 * 1024) {
    isValid = false;
  }
  if (apiTypes.includes(suffix)) {
    isValid = false;
  }
  if (localTypes.includes(suffix)) {
    isValid = false;
  }
  if (isValid) {
    throw new Error('File type is not supported');
  }
};

const checkSize = (file: File, maxSize?: number) => {
  if (file.size > (maxSize ?? 60) * 1024 * 1024) {
    throw new Error('File size exceeds the limit of 60MB');
  }
};

const handleLocal = async (file: File, suffix: string) => {
  if (suffix === 'eps') {
    const convertApi = ConvertApi.auth(
      // @ts-ignore
      import.meta.env.VITE_EPS_TOKEN as string,
    );
    const params = convertApi.createParams();
    params.add('File', file);
    params.add('Timeout', '10');
    const result = await convertApi.convert('eps', 'png', params);
    const url = result.files[0].Url;
    const res = await axios.get(url, {
      responseType: 'blob',
      timeout: 10000,
    });
    return blobToFile(res.data, file.name.replace('.eps', '.png'), 'image/png');
  } else if (suffix === 'psd') {
    const res = await psdjs.fromDroppedFile(file);
    const base64 = res.image.toBase64();
    const blob = base64ToBlob(base64);
    return blobToFile(blob, file.name.replace('.psd', '.webp'), 'image/webp');
  } else if (suffix === 'svg') {
    const base64 = await svg2png(file);
    const blob = base64ToBlob(base64);
    return blobToFile(blob, file.name.replace('.svg', '.webp'), 'image/webp');
  }
};

interface ConverFileOptions {
  maxSize?: number;
}

export const convertFile = async (file: File, opts?: ConverFileOptions) => {
  try {
    let newFile = file;
    let suffix = file.name.split('.').pop() || '';
    checkSize(file, opts?.maxSize);
    checkType(file, suffix);
    if (localTypes.includes(suffix)) {
      newFile = (await handleLocal(file, suffix)) as File;
      suffix = 'webp';
    }
    const res = await uploadFile(newFile);
    const source = res.url;
    let url = '';
    let thumb = '';
    if (ossTypes.includes(suffix) && file.size < 20 * 1024 * 1024) {
      if (basicTypes.includes(suffix)) {
        url = `${source}${getOssSize({ width: 1024 })}`;
        thumb = `${source}${getOssSize({ width: 256 })}`;
      } else {
        url = `${source}${getOssSize({ width: 1024 })}/format,webp`;
        thumb = `${source}${getOssSize({ width: 256 })}/format,webp`;
      }
    } else if (apiTypes.includes(suffix)) {
      url = `${convertAPI}?width=1024&type=webp&url=${source}`;
      thumb = `${convertAPI}?width=256&type=webp&url=${source}`;
    }
    await loadImage(thumb);
    return {
      name: file.name,
      source,
      url,
      thumb,
    };
  } catch (error) {
    return {
      name: file.name,
      source: '',
      url: '',
      thumb: '',
    };
  }
};
